import { CallSetup } from "./call-setup";

export const MEDIA_CAPTURE_TIMEOUT_MS = 30_000;
export function mediaCaptureError(code: string, message: string) {
  return Object.assign(new Error(message), { code });
}

// A timed-out native getUserMedia cannot be cancelled. Keep its slot until it
// settles, so repeated clicks cannot accumulate device requests in the browser.
export class MediaCapture {
  private pending = false;
  constructor(private options: {
    getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
    visible(): boolean;
    timeoutMs?: number;
  }) {}

  checkAvailable() {
    if (!this.options.visible()) throw mediaCaptureError("MICROPHONE_TAB_HIDDEN",
      "Откройте вкладку DoCRM с активным софтфоном и повторите действие: микрофон запрашивается в ней");
    if (this.pending) throw mediaCaptureError("MICROPHONE_BUSY",
      "Предыдущий запрос микрофона ещё ожидает ответа браузера. Проверьте разрешение в активной вкладке");
  }

  async acquire(constraints: MediaStreamConstraints, signal?: AbortSignal): Promise<MediaStream> {
    signal?.throwIfAborted();
    this.checkAvailable();
    this.pending = true;
    const setup = new CallSetup();
    const abort = () => setup.controller.abort(signal?.reason);
    signal?.addEventListener("abort", abort, { once: true });
    const timer = setTimeout(() => setup.controller.abort(mediaCaptureError("MICROPHONE_TIMEOUT",
      "За 30 секунд не удалось получить микрофон. Проверьте разрешение и аудиоустройство")),
      this.options.timeoutMs ?? MEDIA_CAPTURE_TIMEOUT_MS);
    let started = false;
    try {
      return await setup.run(async () => {
        started = true;
        try { return await this.options.getUserMedia(constraints); }
        finally { this.pending = false; }
      }, (stream) => stream.getTracks().forEach((track) => track.stop()));
    } catch (error) {
      const audio = constraints.audio;
      const selectedDevice = typeof audio === "object" && typeof audio.deviceId === "object" &&
        !Array.isArray(audio.deviceId) && audio.deviceId?.exact;
      // Permission for audio:true does not validate a saved, mandatory device ID.
      // Never silently capture another microphone after a headset disappears.
      if (selectedDevice && (error?.name === "NotFoundError" ||
        (error?.name === "OverconstrainedError" && (!error.constraint || error.constraint === "deviceId")))) {
        throw Object.assign(mediaCaptureError("MICROPHONE_DEVICE_UNAVAILABLE",
          "Выбранный микрофон недоступен. В настройках софтфона выберите подключённый микрофон или «По умолчанию»"),
          { name: error.name, cause: error });
      }
      throw error;
    } finally {
      if (!started) this.pending = false;
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
    }
  }
}
